import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListCallTopicsQuery,
  useUpsertCallTopicMutation,
  useDeleteCallTopicMutation,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { CallTopic, CallType } from '../types/models';

interface FormData {
  callType: CallType;
  topic: string;
  date: string;
}

export default function CallTopicsPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();

  const { data: topics = [], isLoading } = useListCallTopicsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const [upsert] = useUpsertCallTopicMutation();
  const [remove] = useDeleteCallTopicMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CallTopic | null>(null);
  const [toDelete, setToDelete] = useState<CallTopic | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<FormData>();

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="Call Topics" />
        <Alert severity="info">Select a project and sprint.</Alert>
      </>
    );
  }

  const openCreate = (callType: CallType = '4PM') => {
    setEditing(null);
    reset({ callType, topic: '', date: format(new Date(), 'yyyy-MM-dd') });
    setOpen(true);
  };

  const openEdit = (t: CallTopic) => {
    setEditing(t);
    reset({ callType: t.callType, topic: t.topic, date: t.date });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await upsert({
        uid,
        projectId,
        sprintId,
        id: editing?.id,
        ...data,
      }).unwrap();
      dispatch(showToast({ message: editing ? 'Topic updated' : 'Topic added', severity: 'success' }));
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, sprintId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Topic deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  const renderColumn = (type: CallType) => {
    const filtered = topics
      .filter((t) => t.callType === type)
      .sort((a, b) => b.date.localeCompare(a.date));
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">{type} Call</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openCreate(type)}
          >
            Add
          </Button>
        </Stack>
        {filtered.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No topics yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {filtered.map((t) => (
              <ListItem
                key={t.id}
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => openEdit(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setToDelete(t)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={t.topic}
                  secondary={<Chip size="small" label={t.date} />}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    );
  };

  return (
    <>
      <PageHeader
        title="Call Topics"
        subtitle="Discussion topics for 4 PM and 8 PM calls."
      />

      {!isLoading && topics.length === 0 ? (
        <EmptyState
          title="No call topics"
          description="Add discussion topics for your daily calls."
          action={<Button variant="contained" onClick={() => openCreate('4PM')}>Add topic</Button>}
        />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderColumn('4PM')}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderColumn('8PM')}
          </Grid>
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit topic' : 'New call topic'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                select
                label="Call"
                defaultValue="4PM"
                {...register('callType', { required: true })}
              >
                <MenuItem value="4PM">4 PM</MenuItem>
                <MenuItem value="8PM">8 PM</MenuItem>
              </TextField>
              <TextField
                label="Topic"
                multiline
                rows={2}
                error={!!errors.topic}
                helperText={errors.topic?.message}
                {...register('topic', { required: 'Required' })}
              />
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register('date', { required: true })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete topic?"
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
